import _ from "lodash";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import { StarsTwoFilled } from "@toddle-edu/ds-icons";

import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import {
  getMessagingV2ReduxState,
  getDebugModePropsForSuggestions,
} from "MessagingV2/modules/MessagingModule";
import {
  MESSAGING_SUGGESTION_PLACE,
  getIsAIDebugModeEnabled,
  getIsMessagingWidget,
  recordSuggestionsClicked,
  scrollOnlyIfNearBottom,
} from "MessagingV2/modules/Utils";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";

import classes from "./AIResponseSuggestions.scss";

const styles = {
  aiStripContainerStyle: {
    borderRadius: "0px 0px 8px 8px",
  },
};

const Loader = ({ length }) => (
  <div className={classes.container}>
    <div className={classes.suggestionsContainer}>
      {_.map(_.range(length), it => (
        <SkeletonLoader
          key={it}
          variant="rounded"
          width="200px"
          height="32px"
        />
      ))}
    </div>
  </div>
);

const AIResponseSuggestions = props => {
  const {
    chatId,
    chatType,
    suggestions,
    lastActiveAt,
    isGeneratingSuggestions,
    isAIResponseStreaming,
    onSuggestionGenerate,
  } = props;

  const { screenMode } = useChatOptions();
  const isAIDebugModeEnabled = getIsAIDebugModeEnabled();
  const isMessagingWidget = getIsMessagingWidget({ screenMode });

  const [aiDebugModeObj, setAIDebugModeObj] = useState(
    getDebugModePropsForSuggestions(chatId) ?? {}
  );

  useEffect(() => {
    if (!isAIDebugModeEnabled) return;
    // Once suggestions are generated/changed, we need to update the debug mode object
    const debugModeProps = getDebugModePropsForSuggestions(chatId) ?? {};
    setAIDebugModeObj(debugModeProps);
  }, [suggestions, chatId, isAIDebugModeEnabled]);

  // whenever new suggestions are generated or chatId changes,
  // if user is already at/near bottom, scroll to bottom
  // to prevent suggestions overlaying on top of chat messages
  useEffect(() => {
    scrollOnlyIfNearBottom({ chatId, behavior: "instant" });
  }, [suggestions, chatId]);

  const handleSuggestionClick = suggestion => {
    if (isAIResponseStreaming) return;
    onSuggestionGenerate(suggestion);
    recordSuggestionsClicked({
      chatType,
      from: MESSAGING_SUGGESTION_PLACE.CHIP,
    });
  };

  if (isGeneratingSuggestions) {
    const isLastActivityOlderThan3Days = moment(lastActiveAt).isBefore(
      moment().subtract(3, "days")
    );

    // we can ignore showing loader if last activity is older than 3 days
    // because there are more chances that this activity is not stale & we'll
    // not get any suggestions for this activity
    return isLastActivityOlderThan3Days ? null : (
      <Loader length={isMessagingWidget ? 1 : 2} />
    );
  }

  if (_.isEmpty(suggestions)) return null;
  const suggestionsToShow = isMessagingWidget
    ? _.take(suggestions, 1)
    : suggestions;

  return (
    <div className={classes.container}>
      <div className={classes.suggestionsContainer}>
        {_.map(suggestionsToShow, suggestion => (
          <Button
            key={suggestion}
            variant="outlined-subtle"
            size="small"
            disabled={isAIResponseStreaming}
            icon={<StarsTwoFilled size="xxx-small" variant="subtle" />}
            onClick={() => handleSuggestionClick(suggestion)}
          >
            <span className={classNames("text-label-3", classes.label)}>
              {suggestion}
            </span>
          </Button>
        ))}
      </div>
      {isAIDebugModeEnabled && !_.isEmpty(aiDebugModeObj) ? (
        <AIFeedbackStrip
          {...aiDebugModeObj}
          containerStyle={styles.aiStripContainerStyle}
        />
      ) : null}
    </div>
  );
};

AIResponseSuggestions.propTypes = {
  chatId: PropTypes.string.isRequired,
  onSuggestionGenerate: PropTypes.func.isRequired,
  isAIResponseStreaming: PropTypes.bool.isRequired,
};

const STABLE_EMPTY_ARRAY = [];
const mapStateToProps = (state, ownProps) => {
  const { chatId } = ownProps;
  const messagingReduxState = getMessagingV2ReduxState(state);
  const { isLoading, suggestions } = _.get(
    messagingReduxState,
    ["aiSuggestions", chatId],
    { isLoading: false, suggestions: STABLE_EMPTY_ARRAY }
  );

  return { isGeneratingSuggestions: isLoading, suggestions };
};

export default connect(mapStateToProps)(AIResponseSuggestions);
